﻿/*!
    *
    * Wijmo Library 5.20251.34
    * https://developer.mescius.com/wijmo
    *
    * Copyright(c) MESCIUS inc. All rights reserved.
    *
    * Licensed under the End-User License Agreement For MESCIUS Wijmo Software.
    * us.sales@mescius.com
    * https://developer.mescius.com/wijmo/licensing
    *
    */
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { VueApi, WjComponentBehavior } from '@mescius/wijmo.vue2.base';
import * as wjcChartRadar from '@mescius/wijmo.chart.radar';
var WjFlexRadarBehavior = /** @class */ (function (_super) {
    __extends(WjFlexRadarBehavior, _super);
    function WjFlexRadarBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexRadarBehavior.prototype._updateControl = function (property, newValue) {
        switch (property) {
            case 'tooltipContent':
                this.control.tooltip.content = newValue;
                break;
            case 'labelContent':
                this.control.dataLabel.content = newValue;
                break;
            default:
                _super.prototype._updateControl.call(this, property, newValue);
        }
    };
    WjFlexRadarBehavior.tag = 'wj-flex-radar';
    WjFlexRadarBehavior.props = [
        'renderEngine',
        'isDisabled',
        'tabOrder',
        'binding',
        'footer',
        'header',
        'selectionMode',
        'palette',
        'plotMargin',
        'footerStyle',
        'headerStyle',
        'tooltipContent',
        'itemsSource',
        'bindingX',
        'interpolateNulls',
        'legendToggle',
        'symbolSize',
        'options',
        'selection',
        'itemFormatter',
        'labelContent',
        'chartType',
        'startAngle',
        'totalAngle',
        'reversed',
        'stacking'
    ];
    WjFlexRadarBehavior.events = [
        'initialized',
        'gotFocus',
        'lostFocus',
        'refreshing',
        'refreshed',
        'invalidInput',
        'rendering',
        'rendered',
        'itemsSourceChanging',
        'itemsSourceChanged',
        'seriesVisibilityChanged'
    ];
    WjFlexRadarBehavior.changeEvents = {
        'selectionChanged': ['selection'],
    };
    WjFlexRadarBehavior.classCtor = function () { return wjcChartRadar.FlexRadar; };
    return WjFlexRadarBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.radar.FlexRadar} control.
 *
 * The <b>wj-flex-radar</b> component may contain
 * the following child components:
 * {@link wijmo.vue2.chart.animation.WjFlexChartAnimation}
 * , {@link wijmo.vue2.chart.radar.WjFlexRadarAxis}
 * , {@link wijmo.vue2.chart.radar.WjFlexRadarSeries}
 * and {@link wijmo.vue2.chart.WjFlexChartLegend}.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.radar.FlexRadar} control it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFlexRadar = WjFlexRadarBehavior.register();
function registerV3WjFlexRadar(app) {
    app.component(WjFlexRadarBehavior.tag, WjFlexRadar);
}
var WjFlexRadarAxisBehavior = /** @class */ (function (_super) {
    __extends(WjFlexRadarAxisBehavior, _super);
    function WjFlexRadarAxisBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexRadarAxisBehavior.tag = 'wj-flex-radar-axis';
    WjFlexRadarAxisBehavior.parentProp = 'axes';
    WjFlexRadarAxisBehavior.props = [
        'wjProperty',
        'axisLine',
        'format',
        'labels',
        'majorGrid',
        'majorTickMarks',
        'majorUnit',
        'max',
        'min',
        'position',
        'reversed',
        'title',
        'labelAngle',
        'minorGrid',
        'minorTickMarks',
        'minorUnit',
        'origin',
        'logBase',
        'plotArea',
        'labelAlign',
        'name',
        'overlappingLabels',
        'labelPadding',
        'itemFormatter',
        'itemsSource',
        'binding',
        'labelMin',
        'labelMax'
    ];
    WjFlexRadarAxisBehavior.events = [
        'initialized',
        'rangeChanged'
    ];
    WjFlexRadarAxisBehavior.classCtor = function () { return wjcChartRadar.FlexRadarAxis; };
    return WjFlexRadarAxisBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.radar.FlexRadarAxis} class.
 *
 * The <b>wj-flex-radar-axis</b> component should be contained in
 * one of the following components:
 * {@link wijmo.vue2.chart.radar.WjFlexRadar}
 *  or {@link wijmo.vue2.chart.radar.WjFlexRadarSeries}.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.radar.FlexRadarAxis} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFlexRadarAxis = WjFlexRadarAxisBehavior.register();
function registerV3WjFlexRadarAxis(app) {
    app.component(WjFlexRadarAxisBehavior.tag, WjFlexRadarAxis);
}
var WjFlexRadarSeriesBehavior = /** @class */ (function (_super) {
    __extends(WjFlexRadarSeriesBehavior, _super);
    function WjFlexRadarSeriesBehavior() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    WjFlexRadarSeriesBehavior.tag = 'wj-flex-radar-series';
    WjFlexRadarSeriesBehavior.parentProp = 'series';
    WjFlexRadarSeriesBehavior.siblingId = 'series';
    WjFlexRadarSeriesBehavior.props = [
        'wjProperty',
        'axisX',
        'axisY',
        'binding',
        'bindingX',
        'cssClass',
        'name',
        'altStyle',
        'symbolMarker',
        'symbolSize',
        'symbolStyle',
        'visibility',
        'itemsSource',
        'interpolateNulls',
        'tooltipContent',
        'itemFormatter',
        'chartType'
    ];
    WjFlexRadarSeriesBehavior.events = [
        'initialized',
        'rendering',
        'rendered'
    ];
    WjFlexRadarSeriesBehavior.changeEvents = {
        'chart.seriesVisibilityChanged': ['visibility'],
    };
    WjFlexRadarSeriesBehavior.classCtor = function () { return wjcChartRadar.FlexRadarSeries; };
    return WjFlexRadarSeriesBehavior;
}(WjComponentBehavior));
/**
 * Vue component for the {@link wijmo.chart.radar.FlexRadarSeries} class.
 *
 * The <b>wj-flex-radar-series</b> component should be contained in
 * a {@link wijmo.vue2.chart.radar.WjFlexRadar} component.
 *
 * The <b>wj-flex-radar-series</b> component may contain
 * a {@link wijmo.vue2.chart.radar.WjFlexRadarAxis} child component.
 *
 * The component supports all properties and events of the pure JavaScript {@link wijmo.chart.radar.FlexRadarSeries} class it represents.
 *
 * The component includes an <b>initialized</b> event that is raised when the control is initialized after it is added to the page.
 * You can use this event to perform further initialization in addition to setting properties in markup.
 * The signature of the handler function is the same as any other Wijmo event handlers.
 */
export var WjFlexRadarSeries = WjFlexRadarSeriesBehavior.register();
function registerV3WjFlexRadarSeries(app) {
    app.component(WjFlexRadarSeriesBehavior.tag, WjFlexRadarSeries);
}
export function registerChartRadar(app) {
    if (VueApi.isV3Plus) {
        registerV3WjFlexRadar(app);
        registerV3WjFlexRadarAxis(app);
        registerV3WjFlexRadarSeries(app);
    }
}
